import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withI18n, withI18nProps } from '@lingui/react'
import compose from 'lodash/fp/compose'
import slice from 'lodash/fp/slice'
import map from 'lodash/fp/map'
import join from 'lodash/fp/join'
import last from 'lodash/last'
import urlFor from 'shared/tools/url-helper'

import prepareComponent from 'client/shared/decorators/prepare-component'
import errorBoundary, {
  ErrorBoundaryProps,
} from 'client/shared/decorators/error-boundary'
import { trimText } from 'client/shared/helpers/text-helpers'
import { authorsAsString } from 'client/bookmate/helpers/author/authorsAsString'
import { getResourceReadersLoader } from 'client/shared/helpers/resource-helpers'

import HeadTitle from 'client/shared/blocks/head-title'
import Meta from 'client/shared/blocks/meta'
import InfiniteScroll from 'client/shared/blocks/infinite-scroll'
import { User } from 'client/bookmate/blocks/user'
import List from 'client/bookmate/blocks/list'

import { getUsersByIds } from 'client/bookmate/reducers/users-reducer'
import { getBookById } from 'client/bookmate/selectors/book-selector'

import { MAX_META_DESCRIPTION_LENGTH } from 'client/shared/constants/metadata'

import { Dispatch, State } from 'shared/types/redux'
import { UserShort } from 'client/shared/types/user'
import { Params } from 'client/shared/types/react-router'
import { BookSerialProps } from 'client/shared/types/resource'
import { QueryParams } from 'shared/tools/url-helper'
import { Helmet } from 'react-helmet-async'

type Props = {
  resource: BookSerialProps
  readers: UserShort[]
  dispatch: Dispatch
  params: Params
  page: number
  currentUserLogin: string
  query?: QueryParams
  url: string
  domain: string
} & ErrorBoundaryProps &
  withI18nProps

const getReadersStr = compose(
  join(', '),
  map(reader => reader.name || reader.login),
  slice(0, 20),
)

class BookReadersBox extends Component<Props> {
  static defaultProps = {
    readers: [],
  }

  load = () => {
    const {
      dispatch,
      params: { uuid },
      page,
      resource,
    } = this.props

    const loadReaders = getResourceReadersLoader[resource.resourceType]
    return dispatch(loadReaders({ uuid, p: page, pp: 20, append: true }))
  }

  render() {
    const { page, readers, currentUserLogin, query } = this.props

    return (
      <div>
        <InfiniteScroll load={this.load} page={page}>
          {this.renderMeta()}
          <List>
            {readers.map((reader, i) => {
              return (
                <User
                  query={query}
                  key={i}
                  user={reader}
                  kind="list"
                  as="li"
                  showPlus={reader.login !== currentUserLogin}
                />
              )
            })}
          </List>
        </InfiniteScroll>
      </div>
    )
  }

  renderMeta() {
    const {
      resource: {
        resourceType,
        title,
        authors,
        readers_count = 0,
        language,
        uuid,
      },
      readers: readersArr,
      i18n,
      domain,
    } = this.props
    const readers = getReadersStr(readersArr)

    const headTitle = i18n._(`book.head_title_readers_${resourceType}`, {
      title,
      authors: authorsAsString(authors),
    })
    const fullDescription = i18n._('book.meta_description_readers', {
      count: readers_count,
      readers,
    })
    const description = trimText(fullDescription, MAX_META_DESCRIPTION_LENGTH)

    return (
      <>
        <HeadTitle>{headTitle}</HeadTitle>
        <Meta name="description" content={description} />
        <Meta name="robots" content="noindex,nofollow" />
        <Helmet
          link={[
            {
              href:
                urlFor.absoluteRootWithSubdomain(language, domain as string) +
                urlFor.bookReaders(uuid),
              rel: 'canonical',
            },
          ]}
        />
      </>
    )
  }
}

const prepareWrapper = prepareComponent(
  async function ({ store, params, router }) {
    const { uuid = '' } = params
    const { dispatch } = store

    const { resourceType } = last(router.routes)

    const loadReaders = getResourceReadersLoader[resourceType]
    await dispatch(loadReaders({ uuid, p: 1, pp: 20 }))
  },
  {
    updateParams: 'uuid',
  },
)

const connectWrapper = connect(
  (state: State, ownProps: { route: { resourceType: string } }) => {
    const { resourceType } = ownProps.route

    return {
      readers: getUsersByIds(state, state[resourceType].readers),
      page: state[resourceType].pages.readers,
      resource: getBookById(state, state[resourceType].uuid),
      currentUserLogin: state.currentUser.data.login,
      query: state.app.storedQuery,
      domain: state.app.domain,
    }
  },
)

const wrappers = compose(
  prepareWrapper,
  connectWrapper,
  errorBoundary,
  withI18n({ update: true }),
)

export default wrappers(BookReadersBox)
